// pause-menu-view.ts — the in-game PAUSE MENU DOM overlay (HTML/DOM UI shell plan,
// Phase 5, Form 2). Renders the kidfInGameMenu 1044 button set on a 160×160
// forms.ini.pp design stage OVER the live canvas, modal (eats all input while up).
// Each button routes the chosen kidc back to onSelect, which the host threads into
// DoInGameMenu's SHARED post-modal action handler (handleInGameMenuChoice) — the
// DOM is presentation-only. This replaces the canvas kidfInGameMenu 1:1.
//
// Geometry is the SHIPPED forms.ini.pp form 1044 (FORM 0 0 160 160, FORMBACKCOLOR,
// center): title "MENU" (62,6) center full-width. EVERY button carries its EXACT
// canvas rect in the descriptor (gameoptions.ts inGameMenuButtons → InGameMenuButton
// {idc,label,x,y,w,h}) — the SP authored rects, or the MP FormatButtons re-stack
// result (Objectives@19, Abort@36, the GAP at y=53 where the now-hidden OPTIONS sat,
// Chat@70, BACK@82,138). This view simply PLACES each button at its rect; there is
// NO view-side auto-stacking or HELP/BACK special-casing — the descriptor is the
// single source of truth for geometry, so the DOM is pixel-faithful to the canvas.
//
// SFX / animation: like the canvas ShellForm (ShellDoModal fAnimate=true) — every
// control cascades in from an alternating side (formchrome.ts zipInStage), then
// ksfxGuiFormShow. The faithful chrome (backdrop + 9-slice buttons) is the SAME
// pack art the DOM menu / objectives overlay use.
//
// The design-stage chrome (formScale/makeStage/at/the 9-slice button/formTitle) is the
// SHARED chrome.ts primitives (Phase 2 de-dup); this view's button uses fontFloor 9.

import type { ButtonArt } from '../title-art.ts';
import { zipInStage } from '../formchrome.ts';
import type { InGameMenuButton } from '../../ui/gameoptions.ts';
import {
  COL_BG,
  COL_FG,
  FONT_BODY,
  buttonWidthU,
  makeStage,
  at,
  formTitle,
  makeStageButton,
  type StageButtonSize,
} from '../chrome.ts';

/** A visible pause-menu button: the canvas control id, its label, and its EXACT
 *  forms.ini.pp design-stage rect (x,y,w,h). The view places each at its rect — no
 *  view-side stacking. Re-exported from the descriptor's InGameMenuButton. */
export type PauseMenuButton = InGameMenuButton;

export interface PauseMenuViewDeps {
  /** the visible buttons with their EXACT canvas rects (gameoptions.ts
   *  inGameMenuButtons → InGameMenuButton); the view places each at its rect. */
  buttons: PauseMenuButton[];
  /** the overlay container (created by the host; this view owns its contents). */
  root: HTMLElement;
  /** a button press → route the kidc through the shared handler (the host unmounts
   *  the overlay first, then calls handleInGameMenuChoice). */
  onSelect: (idc: number) => void;
  /** the title-screen backdrop (titlescreenbkgd.png) as a CSS url; absent ⇒ flat. */
  titleBg?: string;
  /** the raw 9-slice button pieces — faithful chrome; absent ⇒ a CSS-button fallback. */
  buttonArt?: ButtonArt;
  /** play a GUI sfx (ksfxGui*) on a button press, wired to the host SoundMgr. */
  playSfx?: (sfx: number) => void;
}

const FONT = FONT_BODY;

export class PauseMenuView {
  private readonly d: PauseMenuViewDeps;
  /** the shared 9-slice button renderer (owns its own composite cache). */
  private readonly btn: (
    label: string,
    on: () => void,
    size?: StageButtonSize,
  ) => HTMLButtonElement;
  /** the ShellForm zip plays ONCE, on the first show. */
  private animated = false;
  private formShowTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(d: PauseMenuViewDeps) {
    this.d = d;
    // the menu buttons use the larger font floor (9) — see makeStageButton.
    const mk = makeStageButton(d);
    this.btn = (label, on, size) => mk(label, on, size, { fontFloor: 9 });
    const s = d.root.style;
    // a fixed, full-window MODAL overlay above #screen (z above the canvas at 10);
    // pointer-events:all so the canvas behind it gets nothing while it is up.
    s.position = 'fixed';
    s.inset = '0';
    s.display = 'flex';
    s.alignItems = 'center';
    s.justifyContent = 'center';
    s.zIndex = '20';
    s.color = COL_FG;
    s.fontFamily = FONT;
    s.userSelect = 'none';
    s.pointerEvents = 'all';
    s.overflow = 'hidden'; // clip controls parked off-window during the zip (no scrollbar flash)
    // EVERY shell form renders over the title backdrop with a black letterbox
    // (shellform.ts OnPaintBackground) — the menu form is FORMBACKCOLOR.
    s.backgroundImage = d.titleBg !== undefined ? `url(${d.titleBg})` : 'none';
    s.backgroundColor = COL_BG;
    s.backgroundSize = 'cover';
    s.backgroundPosition = 'center';
    s.backgroundRepeat = 'no-repeat';
    window.addEventListener('resize', this.onResize);
    this.render();
  }

  private readonly onResize = (): void => {
    this.render();
  };

  /** detach the resize listener + cancel a pending form-show sfx (the host unmounts
   *  the overlay on a choice). */
  destroy(): void {
    window.removeEventListener('resize', this.onResize);
    if (this.formShowTimer !== null) {
      clearTimeout(this.formShowTimer);
      this.formShowTimer = null;
    }
  }

  // the ShellForm zip-in — the SAME faithful cascade the menu / objectives use
  // (formchrome.ts zipInStage ↔ shellform.ts:298-384).
  private zipIn(stage: HTMLElement): void {
    if (this.formShowTimer !== null) clearTimeout(this.formShowTimer);
    this.formShowTimer = zipInStage(stage, this.d.playSfx);
  }

  // ── the faithful design stage (forms.ini: the menu form is 160×160) ──────────
  // The SAME fixed magnification as the SP menu / objectives overlay (chrome.ts).
  private stageButton(
    s: number,
    label: string,
    x: number,
    y: number,
    w: number,
    h: number,
    on: () => void,
  ): HTMLElement {
    return at(this.btn(label, on, { w: w * s, h: h * s }), s, x, y);
  }

  render(): void {
    const { stage, s } = makeStage();

    // title "MENU" (62,6,35) center kifntTitle — rendered full-width centered (the
    // canvas centers a too-wide title on its anchor; full-width keeps it on-form).
    stage.append(at(formTitle('MENU', s), s, 0, 6, 160));

    // The STACKED menu buttons (everything above the bottom HELP/BACK row) get a UNIFORM
    // width = the WIDEST measured label in the WI button face (+ frame breathing room, via
    // buttonWidthU) so the longest ("STATE OBJECTIVES") fits with padding and they all
    // match — then re-centred on the 160-form. The bottom row (HELP/BACK, at the max y)
    // keeps its authored rect. The descriptor's y/order is preserved; only the stacked
    // widths/x are fit to the wide WI face (fixed authored widths cramped the text).
    const btns = this.d.buttons;
    const maxY = btns.reduce((m, b) => Math.max(m, b.y), 0);
    const stacked = btns.filter((b) => b.y < maxY);
    const uniW = stacked.reduce((m, b) => Math.max(m, buttonWidthU(b.label, b.h, s)), 0);
    for (const b of btns) {
      const isStacked = b.y < maxY;
      const w = isStacked ? uniW : b.w;
      const x = isStacked ? Math.round((160 - w) / 2) : b.x;
      stage.append(this.stageButton(s, b.label, x, b.y, w, b.h, () => this.d.onSelect(b.idc)));
    }

    this.d.root.replaceChildren(stage);
    // The ShellForm zip + ksfxGuiFormShow play on the FIRST show only (ShellDoModal
    // fAnimate=true). Resize re-renders rebuild the stage WITHOUT re-zipping.
    if (!this.animated) {
      this.animated = true;
      this.zipIn(stage);
    }
  }
}
